import {
  useDeleteUserAlbumVideoLikes,
  usePutUserAlbumVideoLikes,
} from '@vimeo/gctl-api/hooks/useUserAlbumVideoLikes';
import { Video } from '@vimeo/gctl-api/models/components/schemas/video/Video';

import { useAlbumStore } from '../state/albumStore';
import { useGetAlbumVideoLikeData } from './use-get-album-video-like-data';

/**
 * Hook to manage the like functionality for a video.
 * @param clipId - The ID of the video to be liked or unliked.
 * @param clipRequestId - The request ID for the clip, used to retrieve the password hash.
 * @param canLike - Whether the user is allowed to like the video.
 * @returns An object containing:
 *   - added: Whether the video is currently liked by the user.
 *   - update: Function to toggle the like state of the video.
 *   - totalLikes: Total number of likes for the video.
 *   - isLikeLoading: Boolean indicating if the like action is currently loading.
 */
export function useVideoLike({
  clipRequestId,
  clipId,
  canLike,
}: {
  clipRequestId: string;
  clipId: string;
  canLike: boolean;
}) {
  const albumId = useAlbumStore((state) => state.albumId);
  const password = useAlbumStore((state) => state.hashedPass);
  const albumOwnerId = useAlbumStore((state) => state.albumOwnerId);

  const { data, isLoading, error, mutate } = useGetAlbumVideoLikeData(clipId);

  // Check if the data contains a 'clip' property and use it if present
  const clip = data && 'clip' in data ? (data as { clip: Video }).clip : undefined;
  const clipData = clip ?? (data as Video | undefined);
  const added = clipData?.metadata?.interactions?.like?.added;
  const totalLikes = clipData?.metadata?.connections?.likes?.total ?? 0;

  const uri = clipData?.metadata?.interactions?.like?.uri;
  const params = new URLSearchParams(uri?.split('?')[1]);
  const auth = params.get('auth');

  const [likeVideo, { loading: isPutLikeLoading, error: isPutLikeError }] =
    usePutUserAlbumVideoLikes();
  const [unlikeVideo, { loading: isUnlikeLoading, error: isUnlikeError }] =
    useDeleteUserAlbumVideoLikes();
  const isError = !added ? isPutLikeError : isUnlikeError;
  const isVideoLikeLoading = !added ? isPutLikeLoading : isUnlikeLoading;

  const update = async () => {
    const isClipDataMissing = !clipData;
    const isLikeNotAllowed = !canLike;
    const hasError = !!error;
    const isLoadingData = isLoading;
    const isUserIdMissing = !albumOwnerId;
    const isVideoIdInvalid = !clipRequestId;

    if (
      isClipDataMissing ||
      isLikeNotAllowed ||
      hasError ||
      isLoadingData ||
      isUserIdMissing ||
      isVideoIdInvalid
    ) {
      return null;
    }

    const requestUpdateLike = !added ? likeVideo : unlikeVideo;
    const newTotal = !added ? totalLikes + 1 : totalLikes - 1;

    await requestUpdateLike({
      where: {
        userId: albumOwnerId,
        albumId: albumId,
        // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
        videoId: clipRequestId,
      },
      query: {
        ...(password && { password }),
        ...(auth && { auth }),
      },
    });

    const updatedClipData = {
      ...data,
      clip: {
        ...clipData,
        metadata: {
          ...clipData.metadata,
          interactions: {
            ...clipData.metadata.interactions,
            like: {
              added: !added,
              uri: `users/${albumOwnerId}/likes/videos/${clipRequestId}`,
            },
          },
          connections: {
            ...clipData.metadata.connections,
            likes: {
              total: newTotal,
              uri: `users/${albumOwnerId}/likes/videos`,
            },
          },
        },
      },
    };

    !isError && mutate(updatedClipData as Video);
  };

  return {
    added,
    update,
    totalLikes,
    isLikeLoading: isLoading || isVideoLikeLoading,
  };
}
